import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { showError, showSuccess } from '../../../../utils/Notifications';
import { LandscapeCommodityResponseDto } from '../../../landscapeAPI/landscape/dto/response/landscape-commodity-response.dto';
import commodityLandscapeService from '../services/commodity-landscape.service';
import { LandscapeCommoditySaveRequestDto } from '../../../landscapeAPI/landscape/dto/request/landscape-commodity-save-request.dto';
import { LandscapeCommodityUpdateRequestDto } from '../../../landscapeAPI/landscape/dto/request/landscape-commodity-update-request.dto';

const initialState = {
  status: 'idle',
  commoditiesLandscape: [] as LandscapeCommodityResponseDto[] | null,
  commodityLandscape: {} as LandscapeCommodityResponseDto | null,
  error: null as string | null,
};

export const fetchCommoditiesByLandscapeId = createAsyncThunk(
  'commoditiesLandscape/fetchCommoditiesByLandscapeId',
  async (landscapeId: string) => {
    return await commodityLandscapeService.fetchCommoditiesByLandscapeId(
      landscapeId
    );
  }
);

export const fetchCommodityLandscapeById = createAsyncThunk(
  'commoditiesLandscape/fetchCommodityLandscapeById',
  async (id: string) => {
    return await commodityLandscapeService.fetchCommodityLandscapeById(id);
  }
);

export const saveCommodityLandscape = createAsyncThunk(
  'commoditiesLandscape/saveCommodityLandscape',
  async (saveRequest: LandscapeCommoditySaveRequestDto) => {
    return await commodityLandscapeService.saveCommodityLandscape(saveRequest);
  }
);

export const updateCommodityLandscape = createAsyncThunk(
  'commoditiesLandscape/updateCommodityLandscape',
  async (
    updateRequest: LandscapeCommodityUpdateRequestDto,
    { rejectWithValue }
  ) => {
    try {
      return await commodityLandscapeService.updateCommodityLandscape(
        updateRequest
      );
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

export const deleteCommodityLandscape = createAsyncThunk(
  'commoditiesLandscape/deleteCommodityLandscape',
  async (
    params: { existingId: string; landscapeId: string },
    { rejectWithValue }
  ) => {
    const { existingId, landscapeId } = params;
    try {
      await commodityLandscapeService.deleteCommodityLandscape(
        existingId,
        landscapeId
      );
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const commoditiesLandscape = createSlice({
  name: 'commoditiesLandscape',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommoditiesByLandscapeId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCommoditiesByLandscapeId.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.commoditiesLandscape = action.payload;
      })
      .addCase(fetchCommoditiesByLandscapeId.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchCommodityLandscapeById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCommodityLandscapeById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.commodityLandscape = action.payload;
      })
      .addCase(fetchCommodityLandscapeById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(saveCommodityLandscape.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveCommodityLandscape.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.commoditiesLandscape?.push(action.payload);
        showSuccess('Commodity added successfully.');
      })
      .addCase(saveCommodityLandscape.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(updateCommodityLandscape.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateCommodityLandscape.fulfilled, (state, action) => {
        state.status = 'succeeded';
        showSuccess('Commodity saved successfully.');
        if (state.commoditiesLandscape) {
          const commodityLandscapeIndex = state.commoditiesLandscape.findIndex(
            (commodityLandscape) => commodityLandscape.id === action.payload.id
          );
          if (commodityLandscapeIndex !== -1) {
            state.commoditiesLandscape[commodityLandscapeIndex] =
              action.payload;
          }
        }
      })
      .addCase(updateCommodityLandscape.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Updating commodity failed. ${action.payload as string}`);
      })
      .addCase(deleteCommodityLandscape.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteCommodityLandscape.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Commodity deleted successfully.');
      })
      .addCase(deleteCommodityLandscape.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Deleting commodity failed. ${action.payload as string}`);
      });
  },
});

export default commoditiesLandscape.reducer;
